/**
 * Currency constants for cross-shop aggregation (ADP-110).
 *
 * When a workspace's shops report in different currencies, the global "all
 * shops" view converts money to USD before summing. Rates here are STATIC —
 * good enough for a directional MVP aggregate.
 *
 * Resolution failures are expressed as `null`, never coerced: a shop whose
 * currency cannot be resolved must be EXCLUDED from the money aggregate (and
 * surfaced as excluded), because folding it in at an assumed rate silently
 * corrupts the totals (e.g. ¥ counted as $ at par).
 *
 * TODO(adp-110): replace RATES_TO_USD with a live FX feed (scheduled refresh +
 * storage) and make the reporting currency a per-workspace setting.
 */

/** Reporting currency used when scoped shops have mixed currencies. */
export const BASE_CURRENCY = "USD" as const;

/**
 * Static conversion rates to USD. Covers the currencies of Amazon Ads
 * marketplaces AdPraxis supports.
 */
export const RATES_TO_USD: Record<string, number> = {
  USD: 1,
  CAD: 0.73,
  MXN: 0.058,
  BRL: 0.2,
  EUR: 1.08,
  GBP: 1.27,
  JPY: 0.0067,
  INR: 0.012,
  AUD: 0.66,
  AED: 0.27,
  SAR: 0.27,
  SGD: 0.74,
  SEK: 0.095,
  PLN: 0.25,
  TRY: 0.03,
  EGP: 0.021,
};

/**
 * Amazon marketplace id → ISO currency, used only as a fallback when a shop's
 * `currency_code` is a legacy null (see amazon_accounts.currency_code).
 */
export const MARKETPLACE_CURRENCY: Record<string, string> = {
  ATVPDKIKX0DER: "USD", // US
  A2EUQ1WTGCTBG2: "CAD", // CA
  A1AM78C64UM0Y8: "MXN", // MX
  A2Q3Y263D00KWC: "BRL", // BR
  A1F83G8C2ARO7P: "GBP", // UK
  A1PA6795UKMFR9: "EUR", // DE
  A13V1IB3VIYZZH: "EUR", // FR
  APJ6JRA9NG5V4: "EUR", // IT
  A1RKKUPIHCS9HS: "EUR", // ES
  A1805IZSGTT6HS: "EUR", // NL
  AMEN7PMS3EDWL: "EUR", // BE
  A2NODRKZP88ZB9: "SEK", // SE
  A1C3SOZRARQ6R3: "PLN", // PL
  A33AVAJ2PDY3EV: "TRY", // TR
  ARBP9OOSHTCHU: "EGP", // EG
  A17E79C6D8DWNP: "SAR", // SA
  A2VIGQ35RCS4UG: "AED", // AE
  A21TJRUUN4KGV: "INR", // IN
  A19VAU5U5O7RUS: "SGD", // SG
  A39IBJ37TRP1C6: "AUD", // AU
  A1VC38T7YXB528: "JPY", // JP
};

/**
 * Resolve a shop's reporting currency: prefer the account's `currency_code`,
 * fall back to its marketplace's currency. Returns `null` when neither
 * resolves — callers must exclude such shops from money aggregation.
 */
export function resolveShopCurrency(input: {
  currencyCode?: string | null;
  marketplaceId?: string | null;
}): string | null {
  const code = input.currencyCode?.trim();
  if (code) return code.toUpperCase();
  const byMarketplace = input.marketplaceId ? MARKETPLACE_CURRENCY[input.marketplaceId] : undefined;
  return byMarketplace ?? null;
}

/**
 * Conversion rate from a currency to USD. Returns `null` for unknown
 * currencies — callers must exclude, never assume par.
 */
export function rateToUsd(currency: string): number | null {
  return RATES_TO_USD[currency.toUpperCase()] ?? null;
}
